/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Fragment, useEffect } from "react";
import { injectIntl } from "react-intl";
import { PrimaryPanelContainer } from "containers/quickTools/Primary/PrimaryPanelContainer";
import { SecondaryPanelContainer } from "containers/quickTools/Secondary/SecondaryPanelContainer";
import QuickToolsCss from "./QuickTools.css";
import { quickToolsPanels } from "constants/enums";
import * as stores from "stores";
import withMediaQuery from "dc-preview-utils/src/components/withMediaQuery";
import { populateQTVerbGroup, wait } from "utils/QuickToolsUtils";
import { QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT } from "constants/constants";
import classNames from "classnames";
import FlyoutPanelContainer from "containers/flyoutPanel/FlyoutPanelContainer";
import { previewEvents } from "publicApis/Events/previewEvents";
import { eventNames } from "publicApis/constants.js";

let cancelSettingMouseOverQuickTools;
const QuickToolsView = props => {
  const {
    id,
    setIsFocusOverQuickTools,
    showPrimaryPanel,
    style,
    shouldUseMobileModernViewer,
    setIsClickedByMouse,
    ...containerProps
  } = props;
  const {
    enableFillSignQuickTools,
    shouldShowCommentingTools,
    previewConfig,
    disabledVerbs,
    activeVerb,
    quickToolsVerbs
  } = containerProps;

  //  show secondary panel only for the verb with show=true
  const showSecondaryPanel = activeVerb && quickToolsVerbs[activeVerb].show;

  useEffect(() => {
    populateQTVerbGroup(previewConfig);
  }, [enableFillSignQuickTools, shouldShowCommentingTools, disabledVerbs]);

  useEffect(() => {
    populateQTVerbGroup(previewConfig, true);
  }, [previewConfig.isCommentingAllowed]);

  useEffect(() => {
    if (shouldUseMobileModernViewer) {
      const verbResetHandler = (previousVerb, currentVerb) => {
        if (previousVerb && !currentVerb) {
          stores.documentStore.setStickyToolsVisibility(true);
          stores.quickToolsStore.setPrimaryPanelVisibility(true);
        }
      };
      previewEvents.on(eventNames.QUICK_TOOL_VERB_CHANGE, verbResetHandler);
    }
  }, []);

  function setActiveFlyoutPanel(value) {
    if (value !== quickToolsPanels.SECONDARY) {
      stores.documentStore.resetSecondaryFlyout();
    }
  }

  function showFlyoutSecondary(show) {
    if (show) {
      setActiveFlyoutPanel(quickToolsPanels.SECONDARY);
    } else {
      setActiveFlyoutPanel(null);
    }
  }

  const mouseEnterHandler = () => {
    cancelSettingMouseOverQuickTools && cancelSettingMouseOverQuickTools();
    setIsFocusOverQuickTools(true);
    setIsClickedByMouse(true);
  };

  const mouseLeaveHandler = () => {
    const waitExecutor = wait(QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT);
    const waitToSetMouseOverQuickTools = waitExecutor.promise;
    cancelSettingMouseOverQuickTools = waitExecutor.cancel;
    waitToSetMouseOverQuickTools
      .then(() => {
        setIsFocusOverQuickTools(false);
      })
      .catch(() => {});
  };

  const keyDownHandler = () => {
    setIsClickedByMouse(false);
  };

  const quickToolsCss = shouldUseMobileModernViewer
    ? classNames(QuickToolsCss.QuickTools, QuickToolsCss.MobileQuickTools)
    : classNames(QuickToolsCss.QuickTools, QuickToolsCss.DesktopQuickTools);
  const renderSecondaryPanel = showSecondaryPanel && (
    <SecondaryPanelContainer
      showFlyout={showFlyoutSecondary}
      shouldUseMobileModernViewer={shouldUseMobileModernViewer}
    />
  );
  const renderMobileFlyoutPanel = (
    <FlyoutPanelContainer activeVerb={activeVerb} shouldUseMobileModernViewer={shouldUseMobileModernViewer}>
      {renderSecondaryPanel}
    </FlyoutPanelContainer>
  );

  return (
    <Fragment>
      <div
        id={id}
        className={quickToolsCss}
        tabIndex={-1}
        role="toolbar"
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
        onKeyDown={keyDownHandler}
        style={style}
      >
        {showPrimaryPanel && (
          <PrimaryPanelContainer {...containerProps} shouldUseMobileModernViewer={shouldUseMobileModernViewer} />
        )}
        {shouldUseMobileModernViewer ? renderMobileFlyoutPanel : renderSecondaryPanel}
      </div>
    </Fragment>
  );
};

export default withMediaQuery(injectIntl(QuickToolsView));
